<template>
  <grid-container-fi
    v-if="assistedService?.user"
    :class="{
      'assisted-service-module-fi': true,
      'assisted-service-module-fi--session-ended': sessionEndedInOtherTab,
    }"
    grid-base-class="fi-grid-cols-12 fi-gap-none"
    :container="false"
  >
    <div class="assisted-service-module-fi__content fi-col-span-12">
      <div
        v-if="!salesforce"
        class="assisted-service-module-fi__label"
      >
        {{ $dict.get('assisted-Service-Module.Module-Label') }}
      </div>
      <div
        v-else
        :aria-controls="isMD ? undefined : 'assisted-service-quote-block'"
        :aria-expanded="isMD ? undefined : showSalesforceInfo"
        :role="isMD ? undefined : 'button'"
        :tabindex="isMD ? undefined : 0"
        class="assisted-service-module-fi__label assisted-service-module-fi__label--toggle"
        @click="toggleSalesforceInfo"
        @keydown.enter.prevent="toggleSalesforceInfo"
        @keydown.space.prevent="toggleSalesforceInfo"
      >
        <span>{{ $dict.get('assisted-Service-Module.Module-Label-Salesforce') }}</span>
        <icon-fi
          :class="{
            'assisted-service-module-fi__toggle': true,
            'assisted-service-module-fi__toggle--active': showSalesforceInfo,
          }"
          icon="chevron-down"
        />
      </div>
      <div
        :class="{
          'assisted-service-module-fi__session': true,
          'assisted-service-module-fi__session--quote': salesforce,
        }"
      >
        <div
          v-if="!salesforce"
          class="assisted-service-module-fi__user-info"
          data-test="assisted-service-session-info"
        >
          <div class="assisted-service-module-fi__user-name-sm">
            {{ assistedService.user.name }}
            -
            {{ assistedService.user.customerId }}
          </div>
          <div class="assisted-service-module-fi__user-uid-sm">
            {{ assistedService.user.displayUid }}
          </div>
          <div class="assisted-service-module-fi__user-md">
            {{ assistedService.user.name }}
            -
            {{ assistedService.user.displayUid }}
            -
            {{ assistedService.user.customerId }}
          </div>
        </div>
        <div
          class="assisted-service-module-fi__quit"
          data-test="end-assisted-service-session"
          @click="onQuit"
        >
          <div class="assisted-service-module-fi__quit-text">
            {{ $dict.get('assisted-Service-Module.Quit') }}
          </div>
          <icon-fi icon="power" />
        </div>
      </div>
    </div>
    <div
      v-if="salesforce"
      id="assisted-service-quote-block"
      ref="quoteBlock"
      :class="{
        'assisted-service-module-fi__salesforce': true,
        'assisted-service-module-fi__salesforce--open': showSalesforceInfo,
      }"
    >
      <div class="assisted-service-module-fi__salesforce__wrapper">
        <div
          class="fi-gap-6 md:fi-gap-12 fi-grid fi-grid-cols-12 fi-px-18 fi-py-12 fi-text-18 fi-text-black fi-semibold fi-text-size-df-16 sm:fi-px-30"
        >
          <!-- Headings row for lg breakpoint -->
          <div
            class="assisted-service-module-fi__label--quote fi-hidden lg:fi-col-span-3 lg:fi-block"
          >
            {{ $dict.get('assisted-Service-Module.Customer-Label') }}
          </div>
          <div
            class="assisted-service-module-fi__label--quote fi-hidden lg:fi-col-span-2 lg:fi-block"
          >
            {{ $dict.get('assisted-Service-Module.Search.Customer-Id') }}
          </div>
          <div
            v-if="salesforce.distributionChannel && salesforce.division"
            class="assisted-service-module-fi__label--quote fi-hidden lg:fi-col-span-2 lg:fi-block"
          >
            {{ $dict.get('assisted-Service-Module.Search.Customer-Distribution') }}
          </div>
          <div
            class="assisted-service-module-fi__label--quote fi-hidden lg:fi-col-span-2 lg:fi-block"
          >
            {{ $dict.get('assisted-Service-Module.Contact') }}
          </div>
          <div
            class="assisted-service-module-fi__label--quote fi-hidden lg:fi-col-span-2 lg:fi-block"
          >
            {{ $dict.get('online-Shop.Global-Labels.Contact-Details.Email') }}
          </div>
          <div
            class="assisted-service-module-fi__label--quote fi-hidden fi-text-right lg:fi-col-span-1 lg:fi-block"
          >
            {{ $dict.get('assisted-Service-Module.Type') }}
          </div>
          <!-- Values row for lg breakpoint -->
          <div class="fi-hidden lg:fi-col-span-3 lg:fi-block">
            {{ assistedService.user?.company?.name }}
          </div>
          <div class="fi-hidden lg:fi-col-span-2 lg:fi-block">
            {{ salesforce.orgUnitId }}
          </div>
          <div
            v-if="salesforce.distributionChannel && salesforce.division"
            class="fi-hidden lg:fi-col-span-2 lg:fi-block"
          >
            {{ salesforce.distributionChannel }} / {{ salesforce.division }}
          </div>
          <div class="fi-hidden lg:fi-col-span-2 lg:fi-block">
            {{ salesforce.cart?.salesforceContact?.firstName }}
            {{ salesforce.cart?.salesforceContact?.lastName }}
          </div>
          <div class="fi-hidden lg:fi-col-span-2 lg:fi-block">
            {{ salesforce.cart?.salesforceContact?.email }}
          </div>
          <div class="fi-hidden fi-text-right lg:fi-col-span-1 lg:fi-block">
            {{ typeDisplay }}
          </div>
          <!-- Combined/stacked fields for smaller breakpoints -->
          <div
            v-if="isSM || assistedService.user?.company?.name || salesforce.orgUnitId"
            class="fi-col-span-full md:fi-col-span-6 lg:fi-hidden"
          >
            <div class="assisted-service-module-fi__label--quote">
              {{ $dict.get('assisted-Service-Module.Customer-Label') }}
            </div>
            <div>
              {{ assistedService.user?.company?.name }} - {{ salesforce.orgUnitId }}
              <template v-if="salesforce.distributionChannel && salesforce.division">
                - {{ salesforce.distributionChannel }} / {{ salesforce.division }}
              </template>
            </div>
          </div>
          <div
            v-if="
              isSM ||
              salesforce.cart?.salesforceContact?.firstName ||
              salesforce.cart?.salesforceContact?.lastName
            "
            class="fi-col-span-full sm:fi-col-span-4 md:fi-col-span-2 lg:fi-hidden"
          >
            <div class="assisted-service-module-fi__label--quote">
              {{ $dict.get('assisted-Service-Module.Contact') }}
            </div>
            <div>
              {{ salesforce.cart?.salesforceContact?.firstName }}
              {{ salesforce.cart?.salesforceContact?.lastName }}
            </div>
          </div>
          <div
            v-if="isSM || salesforce.cart?.salesforceContact?.email"
            class="fi-col-span-full sm:fi-col-span-6 md:fi-col-span-3 lg:fi-hidden"
          >
            <div class="assisted-service-module-fi__label--quote">
              {{ $dict.get('online-Shop.Global-Labels.Contact-Details.Email') }}
            </div>
            <div>{{ salesforce.cart?.salesforceContact?.email }}</div>
          </div>
          <div
            v-if="isSM || typeDisplay"
            class="fi-col-span-full sm:fi-col-span-2 sm:fi-text-right md:fi-col-span-1 lg:fi-hidden"
          >
            <div class="assisted-service-module-fi__label--quote sm:fi-text-right">
              {{ $dict.get('assisted-Service-Module.Type') }}
            </div>
            <div>{{ typeDisplay }}</div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="IS_SHOWROOM && salesforce"
      class="fi-absolute fi-bottom-9 fi-right-9 fi-hidden lg:fi-block"
    >
      <div class="fi-semibold">{{ $dict.get('assisted-Service-Module.Preferred-Order-Unit') }}</div>
      <preferred-order-unit-fi />
    </div>

    <confirm-modal-fi
      ref="sessionEndedModal"
      :headline="$dict.get('assisted-Service-Module.Session-Ended-Other-Tab-Headline')"
      :text="$dict.get('assisted-Service-Module.Session-Ended-Other-Tab')"
      hide-cancel-btn
    />
  </grid-container-fi>
</template>

<script setup lang="ts">
import { IconFi } from 'atoms';
import { onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';

import PreferredOrderUnitFi from '@/components/assisted-service-module-fi/preferred-order-unit-fi/PreferredOrderUnitFi.vue';
import ConfirmModalFi from '@/components/confirm-modal-fi/ConfirmModalFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import { useFischer } from '@/composables/fischerPlugin';
import { useSalesforceInformation } from '@/composables/salesforceInformation';
import type { AssistedServiceData } from '@/lib/fischerPlugin';
import { salesforceItemName } from '@/lib/fischerPlugin';
import { useNavigationStore } from '@/store/pinia/navigation';
import {
  type AssistedServiceMessage,
  createAssistedServiceChannel,
  isMessageFromCurrentTab,
  sendSessionEndedMessage,
} from '@/utils/assistedServiceBroadcast';
import ClientStorage from '@/utils/clientStorage';

import { useAssistedServiceLogin } from './assistedServiceLogin';

defineOptions({
  name: 'AssistedServiceModuleFi',
});

const fischer = useFischer();
const navigationStore = useNavigationStore();
const { postAdminTokenInSession } = useAssistedServiceLogin();
const { typeDisplay } = useSalesforceInformation();
const { isSM, isMD } = useBreakpoints();

const salesforce = ref<any>(null);
const assistedService = ref<AssistedServiceData | null>(null);
const broadcastChannel = ref<BroadcastChannel | null>(null);
const sessionEndedInOtherTab = ref(false);
const showSalesforceInfo = ref(false);
const sessionEndedModal = useTemplateRef('sessionEndedModal');
const quoteBlock = useTemplateRef<HTMLElement>('quoteBlock');

let stopQuoteTracking: (() => void) | undefined;
let publishedQuoteHeight = 0;
let storedQuoteHeight = 0;
let quoteTransitionRunning = false;

const toggleSalesforceInfo = () => {
  showSalesforceInfo.value = !showSalesforceInfo.value;
};

/**
 * How much of the quote block is filled - the optional distribution channel, a contact e-mail that
 * wraps, the stacked layout below `lg` - decides its rendered height, so the offset every
 * `.header-height` element reserves for it cannot be a fixed value.
 *
 * The custom property follows every frame of the collapse transition, because `.header-fi` takes
 * its own `min-height` from it and would otherwise grow before the block had slid into it. The
 * store is held back until the transition settles: each write costs `useHeader` a forced layout to
 * read the header's `offsetHeight`, and the sticky list headers that read `layoutStore.headerHeight`
 * only need the height it settles on. A resize of the block's own content is not a transition and
 * reaches the store right away.
 */
const publishQuoteHeight = (height: number) => {
  // Only write on a real change: the property feeds paddings elsewhere in the page, and an
  // unconditional write could bounce back into the observer through a scrollbar appearing.
  if (height !== publishedQuoteHeight) {
    publishedQuoteHeight = height;
    document.documentElement.style.setProperty('--assisted-service-quote-measured', `${height}px`);
  }

  if (!quoteTransitionRunning && height !== storedQuoteHeight) {
    storedQuoteHeight = height;
    navigationStore.setAssistedServiceQuoteHeight(height);
  }
};

const clearQuoteHeight = () => {
  publishedQuoteHeight = 0;
  storedQuoteHeight = 0;
  quoteTransitionRunning = false;
  navigationStore.setAssistedServiceQuoteHeight(0);
  document.documentElement.style.removeProperty('--assisted-service-quote-measured');
};

const measureQuoteBlock = (element: HTMLElement) =>
  publishQuoteHeight(Math.ceil(element.getBoundingClientRect().height));

/**
 * The element that is measured has to be the collapsible box itself, not the block of fields inside
 * it: while collapsed, that block keeps its full natural height inside a zero-height grid row, so
 * measuring it would reserve the whole quote block's offset for a section the user cannot see.
 *
 * Measuring the collapsible box instead makes the offset follow the expand and collapse transition
 * frame by frame, which is what keeps the header's own `min-height` in step with it while it slides.
 * `transitionrun` and `transitionend` bracket that stretch, so `publishQuoteHeight` can tell an
 * animating height from a settled one - and `transitionend` is the only signal a browser without
 * `ResizeObserver` gets, since the toggle changes the height through a transition and not through a
 * re-render.
 */
watch(quoteBlock, (element) => {
  stopQuoteTracking?.();
  stopQuoteTracking = undefined;

  if (!element) {
    clearQuoteHeight();
    return;
  }

  const measure = () => measureQuoteBlock(element);
  measure();

  const onTransitionRun = (event: TransitionEvent) => {
    if (event.target === element) {
      quoteTransitionRunning = true;
    }
  };
  const onTransitionEnd = (event: TransitionEvent) => {
    if (event.target !== element) {
      return;
    }

    quoteTransitionRunning = false;
    measure();
  };
  element.addEventListener('transitionrun', onTransitionRun);
  element.addEventListener('transitionend', onTransitionEnd);

  const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
  observer?.observe(element);

  stopQuoteTracking = () => {
    element.removeEventListener('transitionrun', onTransitionRun);
    element.removeEventListener('transitionend', onTransitionEnd);
    observer?.disconnect();
  };
});

const onSessionEndedInOtherTab = () => {
  sessionEndedInOtherTab.value = true;
  sessionEndedModal.value?.open();
};

const onQuit = async () => {
  if (!assistedService.value) {
    return;
  }

  // Notify other tabs that the session is ending
  sendSessionEndedMessage();

  const accessToken = fischer.user.accessToken();
  const idToken = fischer.user.idToken();

  const newAssistedService: AssistedServiceData = {
    ...assistedService.value,
    user: undefined,
  };

  try {
    fischer.setAssistedService(newAssistedService);

    if (salesforce.value) {
      try {
        window.sessionStorage.removeItem(salesforceItemName);
      } catch (_error) {
        // noop
      }
      const logoutUrl = fischer.pages.logout().url;
      if (logoutUrl) {
        window.location.href = logoutUrl;
        return;
      } else {
        console.error('missing logout url');
      }
    }

    const { LoginSuccessPage } = await postAdminTokenInSession(accessToken, idToken);
    if (assistedService.value.user) {
      ClientStorage.setValue('impersonatedUser', JSON.stringify(assistedService.value.user), true);
    } else {
      ClientStorage.removeValue('impersonatedUser', true);
    }
    window.location.href = LoginSuccessPage || '/';
  } catch (error) {
    console.error('Error ending assisted service', error);
  }
};

const setSalesforceData = () => {
  salesforce.value = fischer.user.getSalesforce();
  if (salesforce.value) {
    navigationStore.setAssistedServiceSalesforce(true);
  }
};

const handleAssistedServiceSession = () => {
  if (!IS_SSR && assistedService.value) {
    if (fischer.user.authenticated()) {
      const isAssistedServiceActive = Boolean(assistedService.value.user);
      navigationStore.setAssistedServiceActive(isAssistedServiceActive);
      if (isAssistedServiceActive) {
        setSalesforceData();
      }
    } else {
      // session expired, clear assisted service
      fischer.setAssistedService(undefined, undefined, 'module-page-not-authenticated');
    }
  }
};

onMounted(() => {
  if (IS_SSR) return;
  assistedService.value = fischer.user.getAssistedService();
  handleAssistedServiceSession();

  broadcastChannel.value = createAssistedServiceChannel();
  broadcastChannel.value.onmessage = (event) => {
    const message = event.data as AssistedServiceMessage;
    if (message?.type === 'session-ended' && !isMessageFromCurrentTab(message)) {
      onSessionEndedInOtherTab();
    }
  };
});

onBeforeUnmount(() => {
  broadcastChannel.value?.close();
  stopQuoteTracking?.();
  stopQuoteTracking = undefined;
  if (!IS_SSR) {
    clearQuoteHeight();
  }
});
</script>

<style scoped src="./assisted-service-module-fi" />
